import React from "react";
import { withTranslation } from "react-i18next";
import _ from "lodash";
import moment from "moment";
import cx from "classnames";
import submit from "submit-form";
import Chart from "../../parts/chart";
import DateInput from "../../parts/inputs/date-input";
import getDayRangeString from "../../utils/day-range-string";
import ToggleLinksInput from "../../parts/inputs/toggle-links-input";
import CalendarAddExerciseModal from "../../parts/modals/calendar-add-exercise";
import ShareModal from "../../parts/modals/share";
import { mapParameter } from "../../parts/other-exercise/CustomParametersSets";
import { rpeValue } from "../../utils/rpe-range";

const {
  WEIGHT_UNITS,
  DATE_FORMATS: { DATE_PICKER_FORMAT, DATE_DAY_FORMAT },
  PRIMARIES,
  ZONES,
} = require("../../constants");

const ZONES_LEGEND = _.map(ZONES, (name) => {
  return (
    <div key={name} className={`bar series ${name}`}>
      {_.capitalize(name)}
    </div>
  );
});

const PRIMARIES_LEGEND = _.map(PRIMARIES, (name) => {
  return (
    <div key={name} className={`bar series ${name}`}>
      {_.capitalize(name)}
    </div>
  );
});

class View extends React.Component {
  render() {
    const { t } = this.props;
    const { locals } = this.context;
    const {
      query,
      activeExercises,
      activeUser,
      exercisesVolume,
      exercisesVolumeGroupZones,
      exercisesVolumeMean,
      listCompetitions,
    } = locals;
    const { units = locals.units } = query;
    const baselineVolume = locals.fromMetric(
      _.get(exercisesVolumeMean, "total", 0),
      units
    );

    const activeDay = moment(query.day, DATE_PICKER_FORMAT);
    let newExerciseDay;
    if (_.get(query, "modal.type") == "calendar-add-exercise")
      newExerciseDay = query.day;

    const keys = _.range(0, 12);
    const labels = _.map(keys, (i) => `W${i + 1}`);
    const exercisesVolumeGroups = _.values(exercisesVolume);
    const totalVolumes = _.map(keys, (key) =>
      locals.fromMetric(_.sumBy(exercisesVolumeGroups, `${key}.volume`), units)
    );
    const volumeSeries = [
      {
        name: "total",
        className: "total",
        data: totalVolumes,
      },
    ];

    const baselineSeries = _.map(keys, () => baselineVolume);
    const maxVolume = _.max(totalVolumes.concat(baselineVolume));

    const selectedZones =
      query.zoneType === "total"
        ? exercisesVolumeGroupZones
        : _.pick(exercisesVolumeGroupZones, query.zoneType);
    const totalSelectedZoneVolume = locals.fromMetric(
      _(selectedZones).flatMap(_.values).sum(),
      units
    );
    const seriesZones = _(ZONES)
      .map((zone) => {
        return {
          name: zone,
          className: zone,
          value:
            locals.fromMetric(
              _.sumBy(_.values(selectedZones), zone) || 0,
              units
            ) / totalSelectedZoneVolume,
        };
      })
      .filter("value")
      .value();

    const nextCompetition = _(listCompetitions)
      .filter(({ date }) => moment(date).isAfter(moment(), "day"))
      .sortBy("date")
      .first();

    const daysToNextCompetition =
      nextCompetition &&
      moment(nextCompetition.date)
        .endOf("day")
        .diff(moment().startOf("day"), "days");

    return (
      <form id="dashboard-form" className="Dashboard" action="#_">
        <div className="Sub-Header">
          <h1 className="">{activeDay.format("MMMM D, YYYY")}</h1>

          <div className="">
            <DateInput
              name="day"
              placeholder={t("homeDay.datePlaceholder")}
              defaultValue={activeDay}
              onChange={handleDayChange}
            />
          </div>
        </div>

        <div className="Main">
          <div className="section welcome">
            <h4>
              {t("homeDay.greeting", {
                name: _.get(activeUser, "name", "").split(/ +/)[0],
              })}
            </h4>
            {t("homeDay.checkProgress")}
            {nextCompetition && daysToNextCompetition >= 0 ? (
              <div className="competition-tracker">
                <div className="text">{t("homeDay.nextCompetition")}</div>
                {daysToNextCompetition === 0 ? (
                  <div className="days">
                    <b>{t("homeDay.competitionToday")}</b>
                  </div>
                ) : daysToNextCompetition === 1 ? (
                  <div className="days">
                    <b>{t("homeDay.competitionTomorrow")}</b>
                  </div>
                ) : (
                  <div className="days">
                    <b>{daysToNextCompetition}</b>{" "}
                    {t("homeDay.competitionDays", {
                      count: daysToNextCompetition,
                    })}
                  </div>
                )}
                <a
                  href="/dashboard/competitions/"
                  className="text-button icon sm"
                >
                  <img src="/img/svg/icon_competition-2.svg" alt="icon" />
                  {t("homeDay.competitionTracker")}
                </a>
              </div>
            ) : (
              <div className="competition-tracker">
                <a
                  href="/dashboard/competitions/"
                  className="text-button icon sm"
                >
                  <img src="/img/svg/icon_competition-2.svg" alt="icon" />
                  {t("homeDay.competitionTracker")}
                </a>
              </div>
            )}
          </div>

          {!_.isEmpty(activeExercises) ? (
            <div className="section">
              <h4>{t("homeDay.todaysWorkout")}</h4>

              <div className="Workout">
                <div className="head">{t("homeDay.exercises")}</div>

                <div className="details">
                  {_.map(activeExercises, (exercise, i) => {
                    const isAssistant = exercise.type === "assistant";
                    const primary = !isAssistant
                      ? _.get(exercise, "primary")
                      : "Assistant";
                    const defaultRepMax = locals.fromMetric(
                      _.get(
                        exercise,
                        `repMaximums.${primary}`,
                        _.get(activeUser, `repMaximums.${primary}`, 0)
                      )
                    );
                    const equipped =
                      !isAssistant && _.some(exercise.sets, "equipped");
                    const collapsible = !_.isEmpty(exercise.sets);
                    const classname = !isAssistant ? primary : "assistant";

                    return (
                      <div
                        className={cx("Exercise", classname, {
                          equipped,
                          collapsible,
                        })}
                        key={exercise._id}
                      >
                        <input
                          key={query.expand}
                          type="checkbox"
                          id={`exercise-toggle-${exercise._id}`}
                          defaultChecked={query.expand}
                        />

                        <div className="head">
                          {_.capitalize(primary)}{" "}
                          {exercise.secondary &&
                            `(${_.capitalize(exercise.secondary)})`}
                          <label
                            className="expand-toggle"
                            htmlFor={`exercise-toggle-${exercise._id}`}
                          />
                        </div>

                        <div className="details">
                          <div className="details-col-1">
                            {_.map(exercise.sets, (it) => {
                              const equippedRepMax = locals.fromMetric(
                                it.equippedRepMax
                              );
                              const repMax = equippedRepMax || defaultRepMax;
                              const load = locals.fromMetric(it.load);
                              const percentRM = _.round(
                                (load / repMax) * 100,
                                1
                              );
                              const isEquipped = !isAssistant && it.equipped;
                              const hasLoad = Boolean(load);
                              return !exercise.parameters ? (
                                <div className="routine">
                                  <div>
                                    <b>{t("homeDay.sets")}</b> {it.sets}
                                  </div>
                                  <div>
                                    <b>{t("homeDay.reps")}</b> {it.reps}
                                  </div>
                                  {hasLoad && (
                                    <div>
                                      <b>{t("homeDay.load")}</b>{" "}
                                      {locals.withWeight(load)}
                                    </div>
                                  )}
                                  {hasLoad && !isAssistant && (
                                    <div>
                                      <b>{t("homeDay.intensity")}</b>{" "}
                                      {percentRM}%
                                    </div>
                                  )}
                                  {it.rpe > 0 && (
                                    <div>
                                      <b>{t("homeDay.rpe")}</b>{" "}
                                      {rpeValue(it.rpe, it.rpeLower)}
                                    </div>
                                  )}
                                  <div>
                                    <b>{t("homeDay.zone")}</b>{" "}
                                    <span
                                      className={`zone-dot ${it.zone}`}
                                    ></span>
                                  </div>

                                  {isEquipped && (
                                    <div>
                                      <b>{t("homeDay.equipped")}</b>{" "}
                                      {it.equipment}
                                    </div>
                                  )}

                                  {isEquipped && (
                                    <div>
                                      <b>{t("homeDay.equippedRm")}</b>{" "}
                                      {locals.withWeight(equippedRepMax)}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="routine">
                                  {exercise.parameters.map((elem) => (
                                    <div>
                                      <b>{elem}</b> {it[mapParameter(elem)]}
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="foot">
                  <a
                    href={`/dashboard/calendar/?view=day&date=${activeDay.format(
                      DATE_DAY_FORMAT
                    )}`}
                    className="button outline"
                  >
                    {t("homeDay.editWorkout")}
                  </a>
                  <a
                    className="share-workout text-button"
                    href="/actions/refresh?modal[type]=share"
                  >
                    <img src="/img/icons/share-icon.webp" alt="icon" />
                    {t("homeDay.shareWorkout")}
                  </a>
                </div>

                {query.expand ? (
                  <a className="expand-all" href="/actions/refresh?expand=">
                    {t("homeDay.collapseAll")}
                  </a>
                ) : (
                  <a className="expand-all" href="/actions/refresh?expand=true">
                    {t("homeDay.expandAll")}
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="section">
              <h4 className="alt">{t("homeDay.todaysWorkout")}</h4>
              <div className="Dashboard-Weekly-Workouts empty">
                <a
                  className="add"
                  href={`/actions/refresh/?modal[type]=calendar-add-exercise&day=${newExerciseDay}`}
                >
                  {t("homeDay.addSingleWorkout")}
                </a>
              </div>
            </div>
          )}

          <div className="section">
            <h4>{t("homeDay.baselineVolumeTitle")}</h4>
            <div className="Chart-Frame bar-chart">
              <div className="body">
                {!_.isEmpty(exercisesVolume) && (
                  <div>
                    <ToggleLinksInput
                      name="units"
                      options={[
                        { text: "kg", value: "metric" },
                        { text: "lbs", value: "imperial" },
                      ]}
                    />
                    <div className="legend">
                      <div className="bar series total">
                        {t("homeDay.volume")}
                      </div>
                      <div className="line series-a">
                        {t("homeDay.baselineVolume")}
                      </div>
                    </div>
                    <div className="axis-labels">
                      <div className="left">{t("homeDay.volume")}</div>
                    </div>
                    <div className="chart-fullsize">
                      <div className="labels">
                        <Chart
                          type="Bar"
                          only="label"
                          data={{ series: volumeSeries }}
                          options={{
                            width: "60px",
                            height: "380px",
                            stackBars: true,
                            axisX: { showGrid: false, showLabel: false },
                            axisY: {
                              showGrid: false,
                              showLabel: true,
                              onlyInteger: true,
                              scaleMinSpace: 30,
                              low: 0,
                              high: maxVolume,
                            },
                          }}
                        />
                      </div>

                      <div className="data">
                        <Chart
                          type="Bar"
                          data={{
                            labels: labels,
                            series: volumeSeries,
                          }}
                          options={{
                            width: `${_.size(keys) * 40}px`,
                            height: "380px",
                            stackBars: true,
                            chartPadding: { left: 35, right: 75 },
                            axisX: { showGrid: false, showLabel: true },
                            axisY: {
                              showGrid: true,
                              showLabel: false,
                              scaleMinSpace: 30,
                              low: 0,
                              high: maxVolume,
                            },
                          }}
                          tooltip={(item, i) => {
                            const key = keys[i];
                            const date = moment(
                              query.day,
                              DATE_PICKER_FORMAT
                            ).subtract(11 - key, "weeks");
                            const dayRange = getDayRangeString(
                              [
                                date,
                                date.clone().add(1, "week").subtract(1, "day"),
                              ],
                              query
                            );
                            return (
                              <div className="tooltip" style={{ left: item.x }}>
                                <div className="details">
                                  <div>
                                    <b>{dayRange}</b>
                                  </div>
                                  <div>
                                    {t("homeDay.volume")}{" "}
                                    <b>
                                      {locals.withWeight(
                                        totalVolumes[i],
                                        units
                                      )}
                                    </b>
                                  </div>
                                </div>
                              </div>
                            );
                          }}
                        />

                        {_.size(keys) >= 2 && (
                          <Chart
                            type="Line"
                            data={{ series: [baselineSeries] }}
                            options={{
                              height: "380px",
                              showPoint: false,
                              chartPadding: { left: 35, right: 75 },
                              axisX: { showGrid: false, showLabel: true },
                              axisY: {
                                low: 0,
                                high: maxVolume,
                                showGrid: false,
                                showLabel: false,
                                scaleMinSpace: 30,
                              },
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="section">
            <h4>{t("homeDay.zoneIntensityTitle")}</h4>
            <div className="columns">
              <div className="column mobile-hidden" data-col="2/3">
                <div className="Chart-Frame" data-chart="relative-intensity">
                  <div className="body">
                    {!_.isEmpty(exercisesVolumeGroupZones) && (
                      <div>
                        <div className="bg">
                          <div />
                          <div />
                          <div />
                          <div />
                          <div />
                        </div>

                        <Chart
                          type="Line"
                          data={{ series: [_.reverse(seriesZones)] }}
                          options={{
                            fullWidth: true,
                            height: "380px",
                            showPoint: false,
                            lineSmooth:
                              process.browser &&
                              require("chartist").Interpolation.simple(),
                            axisX: { showGrid: false, showLabel: false },
                            axisY: {
                              low: 0,
                              high: 1,
                              stretch: true,
                              showGrid: false,
                              showLabel: false,
                              scaleMinSpace: 75,
                            },
                          }}
                        />

                        <div className="custom-labels">
                          <div className="x-labels">
                            <div>{t("homeDay.percentOfVolume")}</div>
                            {_.reverse(
                              _.map(ZONES, (zone) => {
                                return (
                                  <div key={zone}>{_.capitalize(zone)}</div>
                                );
                              })
                            )}
                          </div>
                          <div className="y-labels">
                            {_.range(0, 110, 10)
                              .reverse()
                              .map((i) => {
                                return <div key={i}>{i}%</div>;
                              })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="column" data-col="1/3">
                <div
                  className="Chart-Frame pie-chart"
                  data-chart="relative-intensity-2"
                >
                  <div className="head">
                    <div className="left-side">
                      <div className="group">
                        <ToggleLinksInput
                          className="tabs"
                          name="zoneType"
                          options={[
                            { text: "Total", value: "total" },
                            { text: "Squat", value: "squat" },
                            { text: "Bench", value: "bench" },
                            { text: "Deadlift", value: "deadlift" },
                          ]}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="body">
                    {!_.isEmpty(exercisesVolume) && (
                      <div>
                        <div className="legend">{ZONES_LEGEND}</div>

                        <div className="chart">
                          <Chart
                            type="Pie"
                            data={{ series: seriesZones }}
                            options={{
                              labelInterpolationFnc: (val, i) => {
                                if (_.get(seriesZones, [i, "name"]) === "empty")
                                  return "";
                                return `${Math.round(val * 100)}%`;
                              },
                              donut: true,
                              donutWidth: 20,
                              donutSolid: true,
                              startAngle: 0,
                              showLabel: true,
                              labelOffset: 27,
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    );
  }
}

function handleDayChange() {
  submit("#_", { body: document.getElementById("dashboard-form") });
}

const HomeDayView = withTranslation("dashboard")(View);

const HomeDay = (app) => {
  app.get("/home", async ({ req, res, locals, load, api, fail }, next) => {
    if (req.query.period === "week") return next();

    locals.heading = ["Athlete", "Dashboard"];
    const { query } = req;
    const { units } = locals;
    const user = locals.activeUser._id;
    const modal = _.get(query, "modal", {});

    _.defaults(query, {
      units,
      zoneType: "total",
      period: "day",
    });

    query.day = query.day && moment(query.day, DATE_PICKER_FORMAT);
    query.day = (
      query.day && query.day.isValid() ? query.day : moment()
    ).format(DATE_PICKER_FORMAT);
    const startDate = moment(query.day, DATE_PICKER_FORMAT).startOf("day");
    const endDate = startDate.clone().add(1, "day").subtract(1, "second");

    await Promise.all([
      load("activeExercises", {
        start: startDate,
        end: endDate,
      }),
      load("exercisesVolume", {
        period: "week",
        start: startDate.clone().subtract(11, "weeks"),
        end: startDate.clone().add(1, "week"),
      }),
      load("exercisesVolumeGroupZones", {
        start: startDate.clone().subtract(11, "weeks"),
        end: startDate.clone().add(1, "week"),
      }),
      load("exercisesVolumeMean", {
        period: "week",
        timeSpan: "6-months",
        day: moment(),
      }),
      load("listCompetitions"),
    ]);

    if (modal.type === "share" && !modal.snapshot) {
      // create share link
      try {
        modal.snapshot = (
          await (
            await api("snapshot", {
              method: "POST",
              body: {
                type: "workout",
                data: JSON.stringify(
                  _.chain(locals)
                    .pick(["activeExercises"])
                    .assign({
                      start: startDate.toJSON(),
                      end: endDate.toJSON(),
                    })
                    .value()
                ),
              },
            })
          ).json()
        )._id;

        modal.date = getDayRangeString([startDate, endDate]);

        return res.refreshWith({ modal });
      } catch (e) {
        console.error(e.stack || e);
        fail(400, "Error creating share link.");
      }
    }

    locals.modal = {
      "calendar-add-exercise": <CalendarAddExerciseModal />,
      share: <ShareModal />,
    }[modal.type];

    res.body = <HomeDayView />;
  });
};

export default HomeDay;
